import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';

import type { AutomationNudgeUser } from '../hooks/useConfluenceAutomationNudgeActions.tsx';
import { useExistingStudioRules } from '../hooks/useExistingStudioRules.ts';
import { existingMeetingNotesRuleExists } from '../hooks/studioRuleMatchers.ts';

import { useMeetingNotesNudgeAction } from './useMeetingNotesNudgeAction.tsx';
import { useMeetingNotesNudgeOnPublishSignal } from './useMeetingNotesNudgeOnPublishSignal.ts';

interface UseMeetingNotesNudgeProps {
	/** The settled, reactive current-user slice from the parent query; supplies the recap email recipient. */
	user?: AutomationNudgeUser | null;
	/** The current content id, used to detect the OnPublish event trigger for this page. */
	contentId: string;
}

/**
 * Entrypoint for the Meeting Notes nudge. Returns the action when the OnPublish signal fires for this
 * content (a meeting notes page was just published — see `useMeetingNotesNudgeOnPublishSignal`).
 *
 * When the user already has a matching meeting notes automation in Studio, the nudge is suppressed
 * (returns `null`). The rules fetch is deferred until the signal actually fires.
 */
export const useMeetingNotesNudge = ({
	user,
	contentId,
}: UseMeetingNotesNudgeProps): ConversationAction | null => {
	const isCandidate = useMeetingNotesNudgeOnPublishSignal(contentId);

	const { rules, isLoading, error } = useExistingStudioRules({
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		skip: !isCandidate || !fg('automations-integration-to-rovo-button-nudges'),
	});

	const hasCreatedMeetingNotesAutomation = existingMeetingNotesRuleExists(rules);

	// Fail closed: withhold until the rules have loaded without error and confirm the user has no
	// matching automation, and until the user (the recap email recipient) has resolved.
	return useMeetingNotesNudgeAction({
		isEnabled:
			isCandidate && !isLoading && !error && !hasCreatedMeetingNotesAutomation && user != null,
		user,
	});
};
